import { SPECIES_DEX } from "@/data/species";

// On-disk character assets: char_surggie_1..12 and char_ceo_1..6 (see public/assets/surggies).
const SURGGIE_MAX = 12;
const CEO_MAX = 6;
const DEFAULT_ASSET = "char_surggie_1";

function assetUrl(key: string): string {
  return `/assets/surggies/${key}.png`;
}

function clampKey(prefix: "char_surggie" | "char_ceo", n: number): string {
  const max = prefix === "char_surggie" ? SURGGIE_MAX : CEO_MAX;
  if (!Number.isInteger(n) || n < 1 || n > max) return DEFAULT_ASSET;
  return `${prefix}_${n}`;
}

/**
 * Resolve any speciesId form to a guaranteed-existing /assets/surggies/char_*.png URL.
 *
 * Handles:
 *  - "char_surggie_N" / "char_ceo_N"  → as-is (range-clamped)
 *  - "surggie_N" / "ceo_N"            → prefixed with char_
 *  - purely numeric ("3")            → SPECIES_DEX id lookup → char_<sprite>
 *  - species name ("Byte")           → SPECIES_DEX name lookup → char_<sprite>
 *  - anything else (incl "default")  → char_surggie_1
 */
export function resolveCharAsset(speciesId: string): string {
  const id = (speciesId ?? "").trim();
  if (id === "") return assetUrl(DEFAULT_ASSET);

  // Already-prefixed char_surggie_N / char_ceo_N
  let m = /^char_(surggie|ceo)_(\d+)$/.exec(id);
  if (m) return assetUrl(clampKey(`char_${m[1]}` as "char_surggie" | "char_ceo", Number(m[2])));

  // Bare surggie_N / ceo_N
  m = /^(surggie|ceo)_(\d+)$/.exec(id);
  if (m) return assetUrl(clampKey(`char_${m[1]}` as "char_surggie" | "char_ceo", Number(m[2])));

  // Purely numeric species id
  if (/^\d+$/.test(id)) {
    const sprite = SPECIES_DEX.find((s) => s.id === Number(id))?.sprite;
    if (sprite) return resolveCharAsset(sprite);
    return assetUrl(DEFAULT_ASSET);
  }

  // Species name
  const sprite = SPECIES_DEX.find((s) => s.name.toLowerCase() === id.toLowerCase())?.sprite;
  if (sprite) return resolveCharAsset(sprite);

  return assetUrl(DEFAULT_ASSET);
}

export const DEFAULT_CHAR_ASSET = assetUrl(DEFAULT_ASSET);
